import { api } from './axios';

export interface LookupItem { id: number; name: string; active: boolean }
export interface LookupKind  { slug: string; label: string }
export interface AcademicYear {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  current: boolean;
  active: boolean;
}
export type ClassDisplayMode = 'GRADES_ONLY' | 'PERCENTAGE_ONLY' | 'BOTH';
export interface SchoolClass {
  id: number;
  name: string;
  sortOrder: number;
  displayMode: ClassDisplayMode;
  active: boolean;
}
export interface ClassSection {
  id: number;
  academicYearId: number;
  academicYearName: string;
  classId: number;
  className: string;
  classSortOrder: number;
  sectionId: number;
  sectionName: string;
}
export interface City { id: number; name: string; area: string | null; active: boolean }

export interface SystemSetting {
  key: string;
  value: string;
  description: string | null;
  updatedBy: number | null;
  updatedAt: string | null;
}

export interface AcademicYearInput {
  name: string;
  startDate: string;
  endDate: string;
  makeCurrent?: boolean;
}
export interface SchoolClassInput {
  name: string;
  sortOrder: number;
  displayMode: ClassDisplayMode;
}
export interface CityInput {
  name: string;
  area?: string | null;
}

export const setupApi = {
  // simple lookups
  listKinds: () => api.get<LookupKind[]>('/setup/lookups').then((r) => r.data),
  listLookup: (slug: string, activeOnly = true) =>
    api.get<LookupItem[]>(`/setup/lookups/${slug}`, { params: { activeOnly } }).then((r) => r.data),
  createLookup: (slug: string, name: string) =>
    api.post<LookupItem>(`/setup/lookups/${slug}`, { name }).then((r) => r.data),
  renameLookup: (slug: string, id: number, name: string) =>
    api.put<LookupItem>(`/setup/lookups/${slug}/${id}`, { name }).then((r) => r.data),
  toggleLookup: (slug: string, id: number, value: boolean) =>
    api.patch<LookupItem>(`/setup/lookups/${slug}/${id}/active`, null, { params: { value } }).then((r) => r.data),

  // academic years
  listAcademicYears: () => api.get<AcademicYear[]>('/setup/academic-years').then((r) => r.data),
  currentAcademicYear: () => api.get<AcademicYear>('/setup/academic-years/current').then((r) => r.data),
  createAcademicYear: (body: AcademicYearInput) =>
    api.post<AcademicYear>('/setup/academic-years', body).then((r) => r.data),
  updateAcademicYear: (id: number, body: AcademicYearInput) =>
    api.put<AcademicYear>(`/setup/academic-years/${id}`, body).then((r) => r.data),
  makeYearCurrent: (id: number) =>
    api.post<AcademicYear>(`/setup/academic-years/${id}/make-current`).then((r) => r.data),

  // classes
  listClasses: (activeOnly = true) =>
    api.get<SchoolClass[]>('/setup/classes', { params: { activeOnly } }).then((r) => r.data),
  createClass: (body: SchoolClassInput) =>
    api.post<SchoolClass>('/setup/classes', body).then((r) => r.data),
  updateClass: (id: number, body: SchoolClassInput) =>
    api.put<SchoolClass>(`/setup/classes/${id}`, body).then((r) => r.data),
  toggleClass: (id: number, value: boolean) =>
    api.patch<SchoolClass>(`/setup/classes/${id}/active`, null, { params: { value } }).then((r) => r.data),

  // class-sections
  listClassSections: (academicYearId?: number) =>
    api.get<ClassSection[]>('/setup/class-sections', { params: { academicYearId } }).then((r) => r.data),
  bulkCreateClassSections: (academicYearId: number, classId: number, sectionIds: number[]) =>
    api.post<ClassSection[]>('/setup/class-sections', { academicYearId, classId, sectionIds }).then((r) => r.data),
  deleteClassSection: (id: number) =>
    api.delete<void>(`/setup/class-sections/${id}`).then((r) => r.data),

  // cities
  listCities: (activeOnly = true) =>
    api.get<City[]>('/setup/cities', { params: { activeOnly } }).then((r) => r.data),
  createCity: (body: CityInput) =>
    api.post<City>('/setup/cities', body).then((r) => r.data),
  updateCity: (id: number, body: CityInput) =>
    api.put<City>(`/setup/cities/${id}`, body).then((r) => r.data),
  toggleCity: (id: number, value: boolean) =>
    api.patch<City>(`/setup/cities/${id}/active`, null, { params: { value } }).then((r) => r.data),

  // system settings
  listSystemSettings: () =>
    api.get<SystemSetting[]>('/setup/system-settings').then((r) => r.data),
  updateSystemSettings: (values: Record<string, string>) =>
    api.put<SystemSetting[]>('/setup/system-settings', { values }).then((r) => r.data),
};
